import { useContext, useEffect, useState } from "react";

import { MapContext } from "../MapProvider";

/**
 * Custom hook to check if the current editing layer is in edit mode.
 * Returns true if the current editingLayerId is in the editingLayerIds array.
 * For sublayers, checks if the sublayer is in the editableSublayers array.
 *
 * @returns Boolean indicating if the current layer is editable
 */
export function useLayerEditable(): boolean {
  const { map, drawControl } = useContext(MapContext);
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    const checkEditability = () => {
      const allLayers = drawControl.current?.options?.editOptions?.allLayers;
      const editingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;
      const editingLayerIds = drawControl.current?.options?.editOptions?.editingLayerIds;
      const editableSublayers = drawControl.current?.options?.editOptions?.editableSublayers;
      const parentLayerId = drawControl.current?.options?.parentLayerId;

      // If no allLayers defined (not in multi-layer mode), always editable
      if (!allLayers || allLayers.length === 0) {
        setIsEditable(true);
        return;
      }

      // If no current editing layer selected, not editable
      if (!editingLayerId) {
        setIsEditable(false);
        return;
      }

      // If editing a sublayer (parentLayerId is set), check editableSublayers
      if (parentLayerId && editableSublayers) {
        const isEditableSublayer = editableSublayers.some((sub) => sub.sublayerId === editingLayerId);
        setIsEditable(isEditableSublayer);
        return;
      }

      // Check if the current editing layer is in the editingLayerIds array
      setIsEditable(editingLayerIds ? editingLayerIds.includes(editingLayerId) : false);
    };

    // Initial check
    checkEditability();

    // Listen for layer changes
    const handleLayerChange = () => {
      checkEditability();
    };

    // Listen for editing layers changes (when edit mode is toggled)
    const handleEditingLayersChange = () => {
      checkEditability();
    };

    map?.on("draw.layerchange", handleLayerChange);
    map?.on("draw.editingLayersChange", handleEditingLayersChange);

    return () => {
      map?.off("draw.layerchange", handleLayerChange);
      map?.off("draw.editingLayersChange", handleEditingLayersChange);
    };
  }, [map, drawControl]);

  return isEditable;
}
